import {html, css, LitElement, CSSResult, PropertyValues} from "lit";
import {customElement, property, queryAssignedElements} from 'lit/decorators.js';

interface ResponsiveVideoSrc {
    src: string
    type: string
}

@customElement('rhit-test')
export class RhitTest extends LitElement {
    protected render() {
        return html `
            <ul>
                <slot name="listitem">empty</slot>
            </ul>
            <slot @slotchange="${this._handleSlotChange}"></slot>
        `;
    }

    _handleSlotChange() {
        this.requestUpdate();
    }


}

@customElement('responsive-video-source')
export class RhitResponsiveVideoSource extends LitElement {

    @property()
    src?: string;

    @property()
    type?: string;

    constructor() {
        super();
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
    }

    protected render() {

        if (this.src && this.type) {
            let videoSource = document.createElement('source');
            videoSource.src = this.src;
            videoSource.type = this.type;
            return html`${videoSource}`;
        }

        return html``;

    }

}

@customElement('responsive-video')
export class RhitResponsiveVideo extends LitElement {

    static styles: CSSResult = css`
        :host {
            max-width: 100%;
            height: auto;
        }
        
        :host > video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
        }
    `;

    @property({type: Object})
    breakpointsDown? = {
        xs: 575.98,
        sm: 767.98,
        md: 991.98,
        lg: 1199.98,
        xl: 1399.98
    }

    @property({type: Object})
    breakpointsUp? = {
        xs: 576,
        sm: 768,
        md: 992,
        lg: 1200,
        xl: 1400
    }

    @property()
    breakpoint?: string = 'lg'

    @property()
    breakpointDirection?: string = 'down'

    @property({type: Array})
    src: ResponsiveVideoSrc[] = []

    @property()
    loop?: string | undefined = '0'

    @property()
    autoplay?: string | undefined = '0'

    @property()
    muted?: string | undefined = '0'

    @property()
    controls?: string | undefined = '0'

    private videoElement: HTMLVideoElement | undefined;

    @queryAssignedElements()
    _videoSourceNodes!: Array<HTMLElement>;

    constructor() {
        super();

    }

    public restartVideo(): void {
        if (this.videoElement) {
            this.videoElement.pause();
            this.videoElement.currentTime = 0;
            let promise = this.videoElement.play();
            if (promise !== undefined) {
                promise.then(_ => {
                    //autoplay
                }).catch(_error => {
                    //error autoplay
                });
            }
        }
    }

    private showVideo(): boolean {
        const breakpoints = this.breakpointDirection && this.breakpointDirection.toLowerCase() == 'up' ? this.breakpointsUp : this.breakpointsDown;
        let breakpointValue = undefined;

        switch (this.breakpoint) {
            case 'xl':
                breakpointValue = breakpoints?.xl
                break;
            case 'md':
                breakpointValue = breakpoints?.md
                break;
            case 'sm':
                breakpointValue = breakpoints?.sm
                break;
            case 'xs':
                breakpointValue = breakpoints?.xs
                break;
            default:
                breakpointValue = breakpoints?.lg;
        }

        if (breakpointValue !== undefined) {
            if (this.breakpointDirection && this.breakpointDirection.toLowerCase() == 'up') {
                if (window.outerWidth < breakpointValue) {
                    return false;
                }
            } else {
                if (window.outerWidth > breakpointValue) {
                    return false;
                }
            }
        }

        return true;
    }

    private autoplayVideo() {
        if (this.stringIsBool(this.autoplay)) {
            let promise = this.videoElement?.play();
            if (promise !== undefined) {
                promise.then(_ => {
                    //autoplay
                }).catch(_error => {
                    //error autoplay
                });
            }
        }
    }

    private stringIsBool(value: string | undefined): boolean {
        if (value === undefined) {
            return false;
        }

        return value.toLowerCase() === 'true' || value.toLowerCase() === '1';
    }

    private isVideoSrcArrayObject(srcArray: any): boolean {
        return Array.isArray(srcArray) && !srcArray.some((srcObj) => !(this.isVideoSrcObject(srcObj)));
    }

    private isVideoSrcObject(srcObj: object): srcObj is ResponsiveVideoSrc {
        return typeof srcObj === 'object' && 'src' in srcObj && 'type' in srcObj && typeof srcObj.src === 'string' && typeof srcObj.type === 'string';
    }

    private createVideo() {
        this.videoElement = document.createElement('video');

        this.videoElement.loop = this.stringIsBool(this.loop);
        this.videoElement.muted = this.stringIsBool(this.muted);
        this.videoElement.controls = this.stringIsBool(this.controls);

        if (this.stringIsBool(this.autoplay)) {
            this.videoElement.muted = true;
            this.videoElement.autoplay = true;
            this.videoElement.playsInline = true;
        }

        this.videoElement.id = 'video';
        this.videoElement.setAttribute('part', 'video');
        this.videoElement.slot = 'video';

        if (this.isVideoSrcArrayObject(this.src)) {
            this.src.forEach((srcObj) => {
                if (this.isVideoSrcObject(srcObj)) {
                    let videoSourceElement: HTMLSourceElement = document.createElement('source');
                    videoSourceElement.type = srcObj.type;
                    videoSourceElement.src = srcObj.src;
                    this.videoElement?.appendChild(videoSourceElement);
                }
            });
        } else {
            console.error('webcomponent responsive video: wrong src format, it has to be [{"src": "...", "type": "..."}, {...}]');
        }

        let slot: HTMLSlotElement = document.createElement('slot');
        slot.name = "source";
        //slot.addEventListener('slotchange', this._handleSlotChange);
        this.videoElement.appendChild(slot);

    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);

        window.addEventListener('resize', () => {
            if (this.showVideo()) {

                if (this.videoElement === undefined) {
                    this.createVideo();
                }

                if (this.videoElement && !this.shadowRoot?.querySelector('video')) {
                    this.shadowRoot?.appendChild(this.videoElement);
                    this.autoplayVideo();
                }
            } else {
                if (this.videoElement && this.shadowRoot?.querySelector('video')) {
                    this.shadowRoot?.removeChild(this.videoElement);
                }
            }
        });

        //console.log(this.shadowRoot?.querySelector('responsive-video-source'));

    }

    _handleSlotChange() {

        let video = this.shadowRoot?.querySelector('video');

        if (video) {
            for (let i = 0; i < this.children.length; ++i) {
                video.appendChild(this.children[i]);
            }

            video.autoplay = this.stringIsBool(this.autoplay);
            video.muted = this.stringIsBool(this.muted);
            video.loop = this.stringIsBool(this.loop);
            video.controls = this.stringIsBool(this.controls);
            video.playsInline = true;
        }
    }

    protected render() {
        if (!this.showVideo()) {
            return html``;
        }

        return html `
            <video id="video">
                <slot @slotchange="${this._handleSlotChange}" name="source"></slot>
            </video>
        `;

    }
}